import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack, Typography } from '@mui/material';
import useStylesFieldForm from 'app/profile/settings/styles/useStylesFieldForm.styles';
import useStylesSettings from 'app/profile/settings/styles/useStylesSettings.styles';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import ActionButton from 'common/components/ActionButton';
import useFlexBoxStyles from 'common/flexBox';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'redux/slices/authSlice';
import { getLicense } from 'redux/slices/licenseSlice';
import { getOrganization, refreshCalendarV3PG, refreshWidgetInteractionsPG } from 'redux/slices/organizationSlice';
import { getSettingsDirty } from 'redux/slices/profileSlice';
import ItemSettings from '../components/ItemSettings';
import { useSettingValue } from '../utils/useSettingValue';
import DashboardAvailableTimezonesSetting from './DashboardAvailableTimezonesSetting';

const DashboardsSettings = (props) => {
    const {
        toggleChange,
        handleSubmit,
        hasChanges,
        dashboardSummary,
        dashboardAvailableTimezones,
        interactionTables,
        sectionPerLevels,
    } = props;

    const { t } = useTranslation();
    const { classes: classes1 } = useStylesFieldForm();
    const { classes: classes2 } = useStylesSettings();
    const { classes: flexBox } = useFlexBoxStyles();
    const classes = { ...classes1, ...classes2 };
    const organization = useSelector(getOrganization);
    const dispatch = useDispatch();
    const settingsDirty = useSelector(getSettingsDirty);
    const user = useSelector(getUser);
    const { enqueueSnackbar } = useSnackbar();
    const isRoot = user.role === 'root';
    const [pendingPG, setPendingPG] = useState(false);
    const license = useSelector(getLicense);
    const hideIChat = !license?.services?.oneToOne?.iChat;
    const newIChat = useSettingValue(SettingsKey.NEW_ICHAT);
    const calendarVersion = useSettingValue(SettingsKey.CALENDAR_VERSION) ?? 3;
    const hideO2O = !license?.services?.oneToOne?.iCall;

    const handleCheckChange = (event, settingName) => {
        toggleChange(settingName, event.target.checked);
    };

    const refreshCalendarV3 = useCallback(() => {
        setPendingPG(true);
        dispatch(
            refreshCalendarV3PG({
                organizationId: organization.id,
                onSuccess: () => {
                    setPendingPG(false);
                    enqueueSnackbar(t('profile.settings.successRefreshCalendarV3', {}), { variant: 'success' });
                },
                onFailure: () => {
                    setPendingPG(false);
                    enqueueSnackbar(t('app.common.error', {}), { variant: 'error' });
                },
            }),
        );
    }, [dispatch, organization, enqueueSnackbar, t]);

    const refreshWidgetInteractions = useCallback(() => {
        setPendingPG(true);
        dispatch(
            refreshWidgetInteractionsPG({
                organizationId: organization.id,
                onSuccess: () => {
                    setPendingPG(false);
                    enqueueSnackbar(t('profile.settings.successRefreshWidgetInteractions', {}), { variant: 'success' });
                },
                onFailure: () => {
                    setPendingPG(false);
                    enqueueSnackbar(t('app.common.error', {}), { variant: 'error' });
                },
            }),
        );
    }, [dispatch, organization, enqueueSnackbar, t]);

    if (!isRoot) {
        return null;
    }

    return (
        <Accordion className={classes.accordion}>
            <AccordionSummary
                expandIcon={<ArrowRightIcon className={classes.accordionIcon} />}
                aria-controls="panel2a-content"
                id="panel2a-header"
            >
                <Typography variant="subtitle1" fontWeight={500}>
                    Dashboards
                </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.eventSection}>
                <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
                    <Box sx={{ display: settingsDirty && hasChanges ? 'inline-flex' : 'none' }}>
                        <Button variant="contained" className={classes.redColor} size="small" onClick={handleSubmit}>
                            {t('Save changes')}
                        </Button>
                    </Box>
                </Box>

                {calendarVersion === 'v3' || !(hideIChat || !newIChat || hideO2O) ? (
                    <Stack mb={2}>
                        <Typography variant="subtitle1" fontWeight={500}>
                            {t('profile.settings.refreshPreagregations')}
                        </Typography>
                        <Stack direction="row" spacing={1} className={flexBox.paddingTop2}>
                            {calendarVersion === 'v3' && (
                                <ActionButton
                                    disabled={pendingPG}
                                    color="primary"
                                    fitWidth
                                    spinner={pendingPG}
                                    onClick={refreshCalendarV3}
                                >
                                    {t('profile.settings.refreshCalendarV3')}
                                </ActionButton>
                            )}

                            {hideIChat || !newIChat || hideO2O ? null : (
                                <ActionButton
                                    disabled={pendingPG}
                                    color="primary"
                                    fitWidth
                                    spinner={pendingPG}
                                    onClick={refreshWidgetInteractions}
                                >
                                    {t('profile.settings.refreshWidgetInteractions')}
                                </ActionButton>
                            )}
                        </Stack>
                    </Stack>
                ) : null}

                <Stack mb={2}>
                    <Typography variant="subtitle1" fontWeight={500}>
                        {t('Immersive on demand')}
                    </Typography>
                    <ItemSettings
                        label={'Enable interaction tables'}
                        onChange={handleCheckChange}
                        name={'interactionTables'}
                        keySettings={SettingsKey.ENABLE_DASHBOARD_IMMERSIVE_ON_DEMAND_INTERACTION_TABLES}
                        value={interactionTables}
                        paddingBottom
                    />

                    <ItemSettings
                        label={'Enable section per levels'}
                        onChange={handleCheckChange}
                        name={'sectionPerLevels'}
                        keySettings={SettingsKey.ENABLE_DASHBOARD_IMMERSIVE_ON_DEMAND_SECTION_PER_LEVELS_TABLES}
                        value={sectionPerLevels}
                        paddingBottom
                    />
                </Stack>

                <Stack mb={2}>
                    <Typography variant="subtitle1" fontWeight={500}>
                        {t('Common settings')}
                    </Typography>

                    <ItemSettings
                        label={'Send dashboard summary emails'}
                        onChange={handleCheckChange}
                        name={'dashboardSummary'}
                        keySettings={SettingsKey.ENABLE_DASHBOARD_SUMMARY_EMAILS}
                        value={dashboardSummary}
                        paddingBottom
                    />

                    <DashboardAvailableTimezonesSetting
                        dashboardAvailableTimezones={dashboardAvailableTimezones}
                        toggleChange={toggleChange}
                    />
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};

export default DashboardsSettings;
